import React, { useMemo } from 'react';
import { Alert } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUndo } from '@fortawesome/pro-solid-svg-icons';
import { isEqual } from 'lodash';
import ManagedInput from 'components/form/managed_input';
import ToggleOverrideSection from '../shared/toggle_override_section';
import { getCountriesForPicker } from 'helpers/countries';

const PICKER_COUNTRIES = getCountriesForPicker();

interface CountryPickerProps {
  allowedCountryIds?: number[] | null;
  defaultCountryIds?: number[] | null;
  selectAllCountryIds?: boolean;
  selectedCountryIds?: number[] | null;
  onUpdateCountry: (payload: {
    countryIds?: number[],
    selectAllCountryIds?: boolean
  }) => void;
}

export default function CountryPicker({
  allowedCountryIds = null,
  defaultCountryIds,
  selectAllCountryIds,
  selectedCountryIds = null,
  onUpdateCountry
}: CountryPickerProps) {
  const currentCountryIds = selectedCountryIds || [];
  // A null list means the parent company didn't narrow the countries down
  const restricted = allowedCountryIds != null;
  const allowed = allowedCountryIds || [];

  // Countries outside the corporate selection can only be unchecked, never
  // checked again, so selections predating the restriction aren't lost.
  const isLocked = (countryId: number) => (
    restricted && !allowed.includes(countryId) && !currentCountryIds.includes(countryId)
  );

  const corporateCountryNames = useMemo(() => (
    PICKER_COUNTRIES.filter(country => allowed.includes(country.id)).map(country => country.label)
  ), [allowedCountryIds]);

  const handleUpdateCountry = (countryId: number, value: boolean) => {
    const newCountryIds = value
      ? [...currentCountryIds, countryId]
      : currentCountryIds.filter(id => id !== countryId);

    onUpdateCountry({
      countryIds: newCountryIds
    });
  };

  const handleSetDefaults = () => {
    const defaults = defaultCountryIds || [];

    onUpdateCountry({
      selectAllCountryIds: false,
      countryIds: restricted ? defaults.filter(id => allowed.includes(id)) : defaults
    });
  };

  const canSetDefault: boolean = useMemo(() => {
    const hasDefaults = !!defaultCountryIds && defaultCountryIds.length > 0;
    const countriesChanged = hasDefaults &&
      !selectAllCountryIds &&
      !isEqual(defaultCountryIds?.sort(), selectedCountryIds?.sort());
    return countriesChanged;
  }, [defaultCountryIds, selectedCountryIds, selectAllCountryIds]);

  const renderDefaultAlert = () => {
    if (!canSetDefault) return null;

    return (
      <Alert className="text-center" variant="info">
        <div>
          The country selection has been edited from the initial configuration.
          Selecting &quot;revert&quot; below will undo those changes,
          and restore the countries selected when this template was created.
        </div>
        <div className="btn btn-ghost btn-sm mt-1" onClick={handleSetDefaults}>
          <FontAwesomeIcon icon={faUndo} />
          Revert Countries
        </div>
      </Alert>
    );
  };

  const renderCorporateScope = () => {
    if (!restricted) return null;

    if (corporateCountryNames.length === 0) {
      return (
        <p className="text-muted mb-0">
          Corporate document defaults don&apos;t include any country, so there is nothing to
          select here. Corporate has to add a country to its own defaults first.
        </p>
      );
    }

    return (
      <div className="mb-3">
        <div>Selected in corporate defaults</div>
        <div className="text-muted">{corporateCountryNames.join(', ')}</div>
      </div>
    );
  };

  return (
    <React.Fragment>
      {renderCorporateScope()}
      <ToggleOverrideSection
        checked={selectAllCountryIds}
        name="country-override"
        subtitle="Unchecking will allow you to select specific countries for this document template"
        title={restricted ? 'Enable for all countries in corporate defaults' : 'Enable for all countries'}
        onOverride={handleSetDefaults}
        onRemove={() => onUpdateCountry({ selectAllCountryIds: true })}
      />
      {renderDefaultAlert()}
      {!selectAllCountryIds && (
        <div className="d-flex flex-column gap-2">
          {PICKER_COUNTRIES.map(country => (
            <ManagedInput
              checked={currentCountryIds.includes(country.id)}
              disabled={isLocked(country.id)}
              key={country.id}
              label={country.label}
              name={`country-${country.id}`}
              type="checkbox"
              onValueChange={(value: boolean) => handleUpdateCountry(country.id, value)}
            >
              {country.label}
            </ManagedInput>
          ))}
        </div>
      )}
    </React.Fragment>
  );
}
